@file:Suppress("unused")

package net.sergeych.ugdx

import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.flow.*
import kotlinx.serialization.Serializable
import net.sergeych.bintools.KVStorage
import net.sergeych.bintools.MemoryKVStorage
import net.sergeych.bintools.defaultNamedStorage
import net.sergeych.bintools.optStored
import net.sergeych.crypto2.*
import net.sergeych.kilogin.client.KiloginClient
import net.sergeych.kilogin.client.KiloginClientSession
import net.sergeych.kilogin.client.LoginState
import net.sergeych.kilogin.client.Registration
import net.sergeych.kilogin.waitUntil
import net.sergeych.kiloparsec.Command
import net.sergeych.kiloparsec.adapter.websocketTransportDevice
import net.sergeych.kiloparsec.command
import net.sergeych.kiloparsec.encodeToUByteArray
import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_tools.globalLaunch
import net.sergeych.mptools.CachedExpression
import net.sergeych.ugdx.shared.*
import net.sergeych.ugdx.shared.api.OrderIdArgs
import net.sergeych.ugdx.shared.api.PushPayload
import net.sergeych.ugdx.shared.api.cmdGetOrder
import net.sergeych.ugdx.shared.api.invoke

/*
 * Copyright (C) 2019-2022 by Sergey S. Chernov (real.sergeych@gmail.com), all rights reserved.
 *
 * Class 2 myonly.cloud registration/login dance.
 *
 * This implementation is exclusively licensed to be used in UnitedAssets project only.
 *
 * Note to developers:
 *
 * This API is consumed by various ugdx client software. The network-exposed part should not be in this file.
 */

var useLocalServer = true
//val localServerUrl = "http://localhost:8090/api/p1"

val internalStorageKey = SymmetricKey(
    blake2b(
        "skeurTv'YRjf67I6UY##l^dkg&ew46F 5dkjk".encodeToUByteArray()
    )
)

val cmdClientPush by command<PushPayload, Unit>()

@Serializable
class RegistrationPayload(
    val mainPublicKey: UniversalPublicKey,
    val inviteCode: String?
)

class GuestAPI private constructor(
    val url: String = "ws://localhost:8090/kp",
    val serviceId: VerifyingPublicKey =
        VerifyingPublicKey.parse("I1po9Y2I7p2aOxeh4nFyGPm3e0YunBEu1Mo-PmIqP84Evg"),
    storage: KVStorage = defaultNamedStorage("ugdx"),
    val testMode: Boolean = false
) : LogTag("GtAPI") {

    companion object {
        val deferredApi = CompletableDeferred<GuestAPI>()

        suspend operator fun invoke() = deferredApi.await()

        val api: GuestAPI get() = deferredApi.getCompleted()

        fun init(
            url: String = "ws://localhost:8090/kp",
            serviceId: VerifyingPublicKey =
                VerifyingPublicKey.parse("I1po9Y2I7p2aOxeh4nFyGPm3e0YunBEu1Mo-PmIqP84Evg"),
            storage: KVStorage = MemoryKVStorage(),
            testMode: Boolean = false
        ) {
            try {
                if( deferredApi.isActive )
                    deferredApi.complete(GuestAPI(url, serviceId, storage, testMode))
            }
            catch (e: Throwable) {
                e.printStackTrace()
                deferredApi.completeExceptionally(e)
            }
        }

    }

    class Session : KiloginClientSession() {

    }

    private val _pushes = MutableSharedFlow<PushPayload>(1024, 0, BufferOverflow.DROP_OLDEST)
    val pushes: SharedFlow<PushPayload> = _pushes.asSharedFlow()

    val storage = MemoryKVStorage()

    var packedSavedState: UByteArray? by storage.optStored()

    val client: KiloginClient<AccountInfo, Session> by lazy {
        KiloginClient.create<AccountInfo, Session>(
            packedSavedState?.let {
                kotlin.runCatching {
                    internalStorageKey.decrypt(it)
                }.getOrNull()
            }
        ) {
            saveState { data ->
                packedSavedState = data?.let { internalStorageKey.encrypt(it) }
            }
            session { Session() }
            connection { websocketTransportDevice(url) }
            local {
                on(cmdClientPush) {
                    _pushes.emit(it)
                }
            }
        }
    }

    /**
     * Here API code posts servce state changes:
     */
    internal val status = MutableStateFlow<ServiceState>(ServiceState.Loading)

    /**
     * The flow ot state changes, see [ServiceState]. It it possible to check current service state via
     * `StateFlow.state` or to collect state changes:
     * ~~~
     * stateFlow.collect { newState ->
     *   println("Service not in state: $newState")
     * }
     * ~~~
     * note that state flow guarantees that collects immediately gets the current state, so _there is no need to check_
     * `flowState.state` if you do collect the flow, you get it anyway.
     */
    val stateFlow: StateFlow<ServiceState> = status.asStateFlow()

    val isLoggedIn: Boolean
        get() =
            stateFlow.value is ServiceState.SignedIn

    private val _loggedInFlow = MutableStateFlow(client.isLoggedIn)
    val loggedInFlow = _loggedInFlow.asStateFlow()

    init {
        globalLaunch {
            client.state.collect {
                when (it) {
                    is LoginState.LoggedIn<*> -> {
                        @Suppress("UNCHECKED_CAST")
                        val ai: AccountInfo = (it as LoginState.LoggedIn<AccountInfo>).clientData.loginData!!
                        status.value = ServiceState.SignedIn(UserAccount(ai))
                        _loggedInFlow.value = true
                    }

                    LoginState.LoggedOut -> {
                        status.value = ServiceState.NotSignedIn
                        _loggedInFlow.value = false
                    }
                }
            }
        }
    }

    /**
     * Check that some login name is available. No guarantees that it will be available on the next call ;)
     * Server tries to reduce "look-alike" login name collisions, so many logins could not be available
     * due to it.
     */
    suspend fun isLoginNameAvailable(login: String): Boolean = client.isLoginNameAvailable(login)

    suspend fun isValidInviteCode(code: String): Boolean = client.call(cmdCheckInviteCode, code)

    /**
     * Perform registration for a given login and password. This is a very long operation as it may include
     * wait time for parsec connection to establish, generating sign in private key (strong) and several
     * network calls.
     */
    suspend fun register(
        login: String, password: String, inviteCode: String? = null,
        passwordComplexity: KDF.Complexity = KDF.Complexity.Moderate,
        secretComplexity: KDF.Complexity = KDF.Complexity.Sensitive,
    ): Registration.Result {
        // new user key. it is stored in secret payload of ACO and public part is
        // registered in the system as the first public key:
        val mainKey = UniversalPrivateKey.new()
        println(client.isLoggedIn)
        println(client.applicationData)

        return client.register(
            login, password,
            RegistrationPayload(mainKey.publicKey, inviteCode),
            passwordComplexity, secretComplexity,
            mainKey
        )
    }

    /**
     * Log in to ugdx environment. The process could take considerable time as it _may include_ parsec establishing
     * and requires sign in key decryption and login proof signing (slow).
     */
    suspend fun signIn(login: String, password: String) =
        client.loginByPassword(login, password)
            // This is ugly but our old. not yet rewritten logic awaits
            // for status to be updated, it is done asynchronously,
            // so we shall wait for it:
            .also {d ->
                if( d != null )
                    status.waitUntil { it is ServiceState.SignedIn }
            }


    val currentAccount: UserAccount?
        get() =
            (status.value as? ServiceState.SignedIn)?.userAccount

    val account: UserAccount
        get() = currentAccount ?: ErrorCode.NOT_SIGNED_IN.raise()

    val userAccountOrThrow: UserAccount get() = currentAccount ?: ErrorCode.NOT_SIGNED_IN.raise()

//    private fun updateAccount(newAccount: UserAccount?) {
//        if (currentAccount != newAccount) {
//            if (newAccount != null) {
//                // turning to signed in
//                if (currentAccount != null) {
//                    println("clearing user data on change acc")
//                    clearUserData()
//                    currentAccount = null
//                }
//                currentAccount = newAccount
//                savedUserAccountInfo = newAccount.accountInfo
//                states.value = ServiceState.SignedIn(newAccount)
//            } else {
//                // changing to signed out
//                println("clearing user data as requested")
//                currentAccount = null
//                clearUserData()
//                states.value = ServiceState.NotSignedIn
//            }
//        }
//    }

    private suspend fun clearUserData() {
        if (client.isLoggedIn) client.logout()
        packedSavedState = null
    }

    suspend fun signOut() {
        // this only starts sequence of logging out
        client.logout()
        // now we want it to be completed:
        status.waitUntil { it == ServiceState.NotSignedIn }
    }

    private val currencyCache = CachedExpression<GetCurrenciesResult>()

    private suspend fun getCurrencyData(): GetCurrenciesResult = currencyCache.get {
        client.call(cmdGetCurrencies)
    }

    /**
     * Request list of supported currencies. This list does not change often, and could be locally cached
     */
    suspend fun getCurrencies(): List<Currency> = getCurrencyData().currencies

    /**
     * Get a currency that is used as a base to quote in [Currency.baseRate].
     */
    suspend fun getBaseCurrency(): Currency = getCurrencyData().baseCurrency

    /**
     * Get the currency by code or throw `NoSuchElementException`
     */
    suspend fun currencyWithCode(code: String) = getCurrencies().first { it.code == code }

    /**
     * Delete test user. If there is a user but it was not created in test mode, will not delete it and will throw
     * exception instead. It is ok to try to delete unexesting user, but it might not work on non-test sites.
     * @return true if the user was ectually deleted
     */
    suspend fun deleteTestUser(loginName: String): Boolean = client.call(cmdDeleteTestUser, loginName)

    /**
     * If there is no logged in user, it will fail, so client should call public [UserAccount] method instead
     */
    internal suspend fun getOrder(orderId: Long): OrderRecord = cmdGetOrder.invoke(OrderIdArgs(orderId))

    suspend fun <A, R> call(command: Command<A, R>, args: A): R = client.call(command, args)

    @Deprecated("use stateFlow.value", replaceWith = ReplaceWith("stateFlow.value"))
    fun refreshStatus() = status.value
}

